import React, { useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Input, Checkbox, CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import { InputChangeEvent } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { Grid, GridColumn, GridRowClickEvent } from '@progress/kendo-react-grid';
import { UserInfo } from '../models/models';
import { CustomWindow } from '../models/custom.window'
import { mapStateToProps, mapDispatchToProps } from '../redux/reduxActions';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { QRCodeSVG } from 'qrcode.react';
import '@progress/kendo-theme-default/dist/all.css';
import './Login.css';
import { PageViewTypeEnum } from '../models/enums';
// Official "Sign in with Microsoft" asset (light theme), downloaded per the Microsoft identity
// platform branding guidelines: https://learn.microsoft.com/en-us/entra/identity-platform/howto-add-branding-in-apps
// Per guidance, the logo/lockup must not be altered, so it is rendered unmodified inside a plain button.
import { ReactComponent as MicrosoftSignInLogo } from './ms-symbollockup_signin_light.svg';

type MfaMethod = 'Email' | 'Authenticator' | '';

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type LoginProps = PropsFromRedux & {
    defaultUserName?: string;
    onSuccess?: (u: UserInfo) => void;
}

type LoginState = {
    statusMessage: string;
    userName: string;
    password: string;
    mFACode: string;
    resetCode: string;
    passwordNew: string;
    passwordChk: string;
    passwordNewValid: boolean;
    passwordNewMsg: string;
    passwordChkValid: boolean;
    passwordChkMsg: string;
    requestSent: boolean;
    useDefaultProject: boolean;
    setDefaultProject: boolean;
    loginBtnEnabled: boolean;
    loginResetBtnEnabled: boolean;
    mFAResetBtnEnabled: boolean;
    showForgotPwd: boolean;
    showProjectSelect: boolean;
    showMFARequest: boolean;
    mFAMethod: MfaMethod;
    mFAEnrollRequired: boolean;
    mFAEnrollSecret: string;
    mFAEnrollOtpAuthUrl: string;
    mFAToken: string;
    selectedPrj: string;
    selectProjectBtnEnabled: boolean;
    usrInf: UserInfo;
    accessToken: string;
    defaultUser: string;
}


declare function invokeSetDefaultUserAction(userDetail: string): void;
declare function invokeOpenSettingsAction(): void;

declare let window: CustomWindow;

const normalizeProjectApiUrls = (userInfo: UserInfo): UserInfo => {
    const configuredUrl = (process.env.REACT_APP_LOGIN_URL || '')
        .replace(/^https?:\/\//i, '')
        .replace(/\/+$/, '');

    if (!configuredUrl) {
        return userInfo;
    }

    const normalizeApiUrl = (apiUrl: string) =>
        typeof apiUrl === 'string' && apiUrl.indexOf('api.tracbase.com.au') !== -1
            ? apiUrl.split('api.tracbase.com.au').join(configuredUrl)
            : apiUrl;

    return {
        ...userInfo,
        userProjects: Array.isArray(userInfo.userProjects)
            ? userInfo.userProjects.map(project => ({
                ...project,
                apiUrl: normalizeApiUrl(project.apiUrl),
            }))
            : userInfo.userProjects,
        currProject: userInfo.currProject ? {
            ...userInfo.currProject,
            apiUrl: normalizeApiUrl(userInfo.currProject.apiUrl),
        } : userInfo.currProject,
    };
};


function Login(props: LoginProps) {

    const loginRef = useRef(null);
    const mfaProjectId = useRef<number | null>(null);
    const [loginPending, setLoginPending] = useState(false);

    let usrName = '';
    let usrPass = '';
    let accessToken = '';

    const [state, setState] = useState<LoginState>({
        statusMessage: '',
        userName: usrName,
        password: usrPass,
        mFACode: '',
        resetCode: '',
        passwordNew: '',
        passwordChk: '',
        loginBtnEnabled: true,
        loginResetBtnEnabled: false,
        mFAResetBtnEnabled: false,
        passwordNewValid: false,
        passwordNewMsg: '',
        passwordChkValid: false,
        passwordChkMsg: '',
        showForgotPwd: false,
        requestSent: false,
        useDefaultProject: true,
        setDefaultProject: false,
        showProjectSelect: false,
        showMFARequest: false,
        mFAMethod: '',
        mFAEnrollRequired: false,
        mFAEnrollSecret: '',
        mFAEnrollOtpAuthUrl: '',
        mFAToken: '',
        selectedPrj: '',
        selectProjectBtnEnabled: false,
        usrInf: new UserInfo(),
        accessToken: accessToken,
        defaultUser: '',
    });

    // Setting the component reference on mount
    useEffect(() => {
        window.loginRef = loginRef;

        return () => {
            // Cleanup reference on unmount
            window.loginRef = null;
        };
    }, []);

    useEffect(() => {
        const handleStorageChange = () => {
            const defaultUser = sessionStorage.getItem("defaultUser");
            setState(prevState => ({
                ...prevState,
                userName: defaultUser!,
            }));
        };
    
        window.addEventListener("defaultUserSet", handleStorageChange);
    
        // Initial load
        handleStorageChange();
    
        return () => window.removeEventListener("defaultUserSet", handleStorageChange);
    }, []);

    
    
    const { instance, accounts } = useMsal();

    const loginScopes = ["api://181eae50-46a8-4a4b-a888-e954363c34c4/Tcb.ReadWrite"];

    const microsoftLoginClick = async () => {
        if (loginPending) return;

        setLoginPending(true);
        state.statusMessage = 'Logging in...';
        setState({ ...state });
        normalizeViewportAfterLogin();

        try {
            await instance.initialize();

            const account = instance.getActiveAccount() || accounts[0];
            let response;
            if (account) {
                try {
                    response = await instance.acquireTokenSilent({ scopes: loginScopes, account });
                } catch (error) {
                    if (!(error instanceof InteractionRequiredAuthError)) throw error;
                    response = await instance.loginPopup({ scopes: loginScopes, account });
                }
            } else {
                response = await instance.loginPopup({ scopes: loginScopes });
            }

            instance.setActiveAccount(response.account);
            state.accessToken = response.accessToken;
            state.loginBtnEnabled = false;
            setState({ ...state });
            await executeMSALLogin(response.accessToken);
        } catch (error) {
            state.statusMessage = 'Microsoft sign-in failed: ' + (error instanceof Error ? error.message : String(error));
            state.accessToken = '';
            state.loginBtnEnabled = true;
            setState({ ...state });
        } finally {
            setLoginPending(false);
        }
    };

    // // Prevent the background page from scrolling or moving when modal/dialog sections are visible
    // useEffect(() => {
    //     const modalOpen = state.showProjectSelect || state.showMFARequest || state.showForgotPwd;
    //     if (!modalOpen) return;

    //     const previousOverflow = document.body.style.overflow;

    //     // Disable body scroll
    //     document.body.style.overflow = 'hidden';

    //     // Also prevent touchmove on mobile from scrolling the background, but allow
    //     // touch scrolling when the touch originates inside the dialog or the project list.
    //     const preventTouch = (e: TouchEvent) => {
    //         try {
    //             const tgt = e.target as Element | null;
    //             if (tgt) {
    //                 // Allow touches that occur inside the login dialog or the project list container
    //                 if (tgt.closest('#loginDlg') || tgt.closest('.projectListContainer')) {
    //                     return; // allow scrolling within the dialog/list
    //                 }
    //             }
    //         } catch (err) {
    //             // ignore and prevent by default
    //         }
    //         e.preventDefault();
    //     };

    //     document.addEventListener('touchmove', preventTouch, { passive: false });

    //     return () => {
    //         // Restore previous state
    //         document.body.style.overflow = previousOverflow || '';
    //         document.removeEventListener('touchmove', preventTouch);
    //     };
    // }, [state.showProjectSelect, state.showMFARequest, state.showForgotPwd]);

    //Cant get this to work
    
    // const invokeStartupActions = () => {

    //     alert("invokeStartupActios");

    //     try {
    //         if (typeof invokeGetDefaultUserAction !== 'undefined') {
    //             invokeGetDefaultUserAction('window.loginRef.setDefaultUser');
    //         }
    //     }
    //     catch (e) {
    //         alert(e);
    //     }
    // }


    const userNameChanged = (event: InputChangeEvent) => {

        state.statusMessage = '';
        state.userName = event.value;
        state.loginBtnEnabled = (event.value !== '' && state.password !== '');
        setState({ ...state });

    }

    const passwordChanged = (event: InputChangeEvent) => {

        state.statusMessage = '';
        state.password = event.value;
        state.loginBtnEnabled = (event.target.value !== '' && state.userName !== '');
        setState({ ...state });

    }

    const passwordNewChanged = (event: InputChangeEvent) => {
        validateNewPasswords(event.value, state.passwordChk, state.resetCode);
    }

    const passwordChkChanged = (event: InputChangeEvent) => {
        validateNewPasswords(state.passwordNew, event.value, state.resetCode);
    }

    const validateNewPasswords = (newPwd: string, chkPwd: string, resetCode: string) => {
        let newPwdTooltip = '';
        let chkPwdTooltip = '';
        if (newPwd === '')
            newPwdTooltip = "Password is required";
        else if (newPwd !== chkPwd)
            newPwdTooltip = "New Password not same as Check Password";
        if (chkPwd === '')
            chkPwdTooltip = "Password is required";
        else if (newPwd !== chkPwd)
            chkPwdTooltip = "New Password not same as Check Password";


        state.statusMessage = '';
        state.passwordNew = newPwd;
        state.passwordChk = chkPwd;
        state.loginResetBtnEnabled = (newPwd !== '' && chkPwd !== '' && state.resetCode !== '') && (newPwd === chkPwd);
        state.passwordNewValid = (newPwd !== '' && newPwd === chkPwd);
        state.passwordNewMsg = newPwdTooltip;
        state.passwordChkValid = (chkPwd !== '' && chkPwd === newPwd);
        state.passwordChkMsg = chkPwdTooltip;
        setState({ ...state });

    }

    const resetCodeChanged = (event: InputChangeEvent) => {

        state.statusMessage = '';
        state.resetCode = event.value;
        state.loginResetBtnEnabled = (event.value !== '' && state.passwordNew !== '' && state.passwordChk !== '') && (state.passwordNew === state.passwordChk);
        setState({ ...state });

    }

    const mFACodeChanged = (event: InputChangeEvent) => {

        state.statusMessage = '';
        state.mFACode = event.value;
        state.loginResetBtnEnabled = (event.value !== '');
        setState({ ...state });

    }

    // Applies the MFA challenge details (method, enrollment info) returned alongside a "CHALLENGE" callStatus
    const applyMfaChallenge = (loginResult: any, projectId: number | null = null) => {
        const result = loginResult?.mfaChallengeResult;
        mfaProjectId.current = projectId;
        if (loginResult?.username) {
            state.userName = loginResult.username;
        }
        state.showMFARequest = true;
        state.mFAMethod = (result?.mfaMethod === 'Authenticator') ? 'Authenticator' : 'Email';
        state.mFAEnrollRequired = !!result?.enrollRequired;
        state.mFAEnrollSecret = result?.manualEntryKey || '';
        state.mFAEnrollOtpAuthUrl = result?.otpAuthUrl || '';
        state.mFAToken = loginResult?.token || '';
    }

    // Normalize the viewport after pressing a login action.
    // Some password managers (eg. LastPass) inject a `div[data-lastpass-root]` which can
    // extend the page height and allow the background to scroll. When the user presses
    // a login button we'll remove/hide that element and clamp the html/body heights to
    // the viewport so the screen doesn't get larger than the device viewport.
    const normalizeViewportAfterLogin = () => {
        try {
            // Remove any LastPass root nodes inserted into the DOM
            const lastpassNodes = document.querySelectorAll('div[data-lastpass-root]');
            lastpassNodes.forEach((n) => {
                try {
                    if (n && n.parentNode) n.parentNode.removeChild(n);
                } catch (e) {
                    // ignore
                }
            });

            // Clamp the document to the viewport height to avoid extra scrolling caused by injected elements
            const html = document.documentElement as HTMLElement;
            const body = document.body as HTMLElement;

            // Store previous values if needed (not persisted here; small transient change)
            html.style.height = '100vh';
            body.style.height = '100vh';
            // Also temporarily hide overflow to be safe
            const prevHtmlOverflow = html.style.overflow;
            const prevBodyOverflow = body.style.overflow;
            html.style.overflow = 'hidden';
            body.style.overflow = 'hidden';

            // Restore overflow after a short delay so app navigation can continue normally
            setTimeout(() => {
                try {
                    html.style.overflow = prevHtmlOverflow || '';
                    body.style.overflow = prevBodyOverflow || '';
                    html.style.height = '';
                    body.style.height = '';
                } catch (e) {
                    // ignore
                }
            }, 1200);
        }
        catch (err) {
            console.warn('normalizeViewportAfterLogin error', err);
        }
    }

    const loginClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (loginPending) return;
        setLoginPending(true);

        state.statusMessage = 'Logging in...';
        state.loginBtnEnabled = false;
        setState({ ...state });

        // Ensure any injected password-manager elements are removed and the viewport is normalized
        normalizeViewportAfterLogin();

        executeLogin();
    }

    const keyDownAction = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            if (state.loginBtnEnabled && !loginPending) {
                setLoginPending(true);

                state.statusMessage = 'Logging in...';
                state.loginBtnEnabled = false;
                setState({ ...state });

                executeLogin();
            }
        }
    }

    const loginResetClick = (event: React.MouseEvent<HTMLButtonElement>) => {

        state.statusMessage = 'Resetting password and logging in...';
        state.loginResetBtnEnabled = false;
        state.showForgotPwd = false;
        setState({ ...state });

        normalizeViewportAfterLogin();

        executeResetLogin();
    }

    const mFAResetClick = (event: React.MouseEvent<HTMLButtonElement>) => {

        state.statusMessage = 'Logging in...';
        state.loginResetBtnEnabled = false;
        setState({ ...state });

        normalizeViewportAfterLogin();

        executeResetMFA();
    }

    const useDefProjChanged = (e: CheckboxChangeEvent) => {

        state.useDefaultProject = e.value;
        setState({ ...state });

    }

    const forgotPwdClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (state.showForgotPwd) {

            state.showForgotPwd = false;
            state.resetCode = '';
            state.statusMessage = '';
            setState({ ...state });

        } else {

            state.showForgotPwd = true;
            state.password = '';
            state.resetCode = '';
            state.passwordChk = '';
            state.statusMessage = '';
            setState({ ...state });

        }
    }

    const sendResetCodeClick = (event: React.MouseEvent<HTMLButtonElement>) => {

        state.statusMessage = '';
        setState({ ...state });

        let url = process.env.REACT_APP_LOGIN_URL + 'api/RequestResetCode';

        let body = {
            username: state.userName,
        };

        fetch(url, { method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case "OK":

                        state.requestSent = true;

                        break;

                    default:

                        state.statusMessage = 'Reset code request failed : ' + res.callStatusMessage;

                };
                setState({ ...state });

            })
            .catch(err => {

                state.statusMessage = 'Error : ' + err.toString();
                state.loginBtnEnabled = true;
                setState({ ...state });

            });
    }

    const resendMFACodeClick = (event: React.MouseEvent<HTMLButtonElement>) => {

        state.statusMessage = '';
        setState({ ...state });

        let url = process.env.REACT_APP_LOGIN_URL + 'api/ResendMFACode';

        let body = {
            username: state.userName,
        };

        fetch(url, { method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case "OK":

                        break;

                    default:

                        state.statusMessage = 'Resend code request failed : ' + res.callStatusMessage;
                        setState({ ...state });

                }
            })
            .catch(err => {
                state.statusMessage = 'Error : ' + err.toString();
                state.loginBtnEnabled = true;
                setState({ ...state });
            });
    }

    const handleLoginSuccess = (uinf: UserInfo, projectSelected = false) => {
        uinf.locale = 'en-AU';

        if (typeof invokeSetDefaultUserAction !== 'undefined') {
            invokeSetDefaultUserAction(uinf.username);
        }

        if ((!state.useDefaultProject && !projectSelected) || !uinf.currProject) {
            showProjectSelect(uinf);
        } else {
            props.updateUserInfo(uinf);
            if (props.onSuccess) props.onSuccess(uinf);
        }
    }

    const executeMSALLogin = (token: string) => {
        let url = process.env.REACT_APP_LOGIN_URL + 'api/MSALLogin';

        let body = {
            useDefPrj: state.useDefaultProject,
        }

        return fetch(url, { method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token } })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case "OK":
                        let uinf: UserInfo = normalizeProjectApiUrls({ ...res.result });
                        handleLoginSuccess(uinf);

                        break;

                    case "CHALLENGE":

                        applyMfaChallenge(res.result);

                        break;

                    case "NO_SSO":
                        state.statusMessage = 'Microsoft sign-in is not enabled for this user. Please use your TracBASE username and password.';
                        state.accessToken = '';
                        state.loginBtnEnabled = true;

                        break;

                    default:

                        state.statusMessage = 'Microsoft sign-in failed: ' + res.callStatusMessage;
                        state.accessToken = '';
                        state.loginBtnEnabled = true;

                };
                setState({ ...state });
            })
            .catch(err => {
                state.statusMessage = 'TracBASE is currently unavailable.  This may be due to system maintenance, or a connectivity problem.  If you have not been made aware of an outage then please contact your system administrator.';
                state.loginBtnEnabled = true;
                state.accessToken = '';
                setState({ ...state });
            });
    }


    const executeLogin = () => {
        let url = process.env.REACT_APP_LOGIN_URL + 'api/Login';

        let body = {
            username: state.userName,
            password: state.password,
            useDefPrj: state.useDefaultProject,
        }

        fetch(url, { method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case "OK":
                        let uinf: UserInfo = normalizeProjectApiUrls({ ...res.result });
                        handleLoginSuccess(uinf);

                        break;

                    case "CHALLENGE":

                        applyMfaChallenge(res.result);

                        break;

                    default:

                        state.statusMessage = 'Login failed : ' + res.callStatusMessage;
                        state.loginBtnEnabled = true;

                };
                setState({ ...state });
            })
            .catch(err => {
                state.statusMessage = 'Unable to log in.  This may be due to system maintenance, or a connectivity problem.  If you have not been made aware of an outage then please contact your system administrator.';
                state.loginBtnEnabled = true;
                setState({ ...state });
            })
            .finally(() => setLoginPending(false));
    }

    const showProjectSelect = (uinf: UserInfo) => {

        const currId = uinf.currProject ? uinf.currProject.projectId : null;
        uinf.userProjects = uinf.userProjects.map(project => ({
            ...project,
            selected: project.projectId === currId,
        }));
        state.usrInf = uinf;
        state.userName = uinf.username;
        state.selectProjectBtnEnabled = (currId !== null);
        state.showProjectSelect = true;
        state.statusMessage = '';
        setState({ ...state });

    }

    const executeResetLogin = () => {
        let url = process.env.REACT_APP_LOGIN_URL + 'api/LoginReset';

        let body = {
            username: state.userName,
            password: state.passwordNew,
            resetCode: state.resetCode
        }

        fetch(url, { method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case "OK":
                        let uinf: UserInfo = normalizeProjectApiUrls({ ...res.result });
                        props.updateUserInfo(uinf);
                        if (props.onSuccess) props.onSuccess(uinf);
                        break;

                    default:

                        state.statusMessage = 'Reset failed : ' + res.callStatusMessage;
                        state.loginBtnEnabled = true;
                        setState({ ...state });

                }
            })
            .catch(err => {
                state.statusMessage = 'Error : ' + err.toString();
                state.loginBtnEnabled = true;
                setState({ ...state });
            });
    }

    const executeResetMFA = () => {
        let url = process.env.REACT_APP_LOGIN_URL + 'api/MFAReset';

        let body = {
            username: state.userName,
            resetCode: state.mFACode
        }

        fetch(url, { method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + state.mFAToken } })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case "OK":
                        let uinf: UserInfo = normalizeProjectApiUrls({ ...res.result });
                        state.showMFARequest = false;
                        state.mFACode = '';
                        state.mFAToken = '';

                        const projectId = mfaProjectId.current;
                        mfaProjectId.current = null;
                        if (projectId !== null && uinf.currProject?.projectId !== projectId) {
                            // MFAReset can return the default project instead of the one already selected.
                            const project = uinf.userProjects.find(project => project.projectId === projectId);
                            if (project) {
                                selectProjectClick(undefined, { ...uinf, currProject: project });
                            } else {
                                showProjectSelect(uinf);
                            }
                        } else {
                            handleLoginSuccess(uinf, projectId !== null);
                        }
                        setState({ ...state });
                        break;

                    default:
                        state.statusMessage = 'Reset failed : ' + res.callStatusMessage;
                        state.loginBtnEnabled = true;
                        state.loginResetBtnEnabled = true;
                        setState({ ...state });

                }
            })
            .catch(err => {
                state.statusMessage = 'Error : ' + err.toString();
                state.loginBtnEnabled = true;
                state.loginResetBtnEnabled = true;
                setState({ ...state });
            });
    }

    const renderListItem = (e: any) => {
        let data = e.dataItem;
        return (<div>{data.projectName}</div>);
    }

    // Allow calling selectProjectClick with an optional UserInfo parameter so
    // the double-click handler can select & continue immediately.
    const selectProjectClick = (event?: React.MouseEvent<HTMLButtonElement>, uinfParam?: UserInfo) => {
        let uinf = uinfParam || state.usrInf;
        //let sel = uinf.userProjects.find(x => x.selected === true);
        //if (sel) {

        if (!uinf.currProject) {
            state.statusMessage = 'Please select a project';
            setState({ ...state });
            return;
        }

        let url = process.env.REACT_APP_LOGIN_URL + 'api/ChangeUserProject';

        let body = {
            username: state.userName,
            projectId: uinf.currProject.projectId,
            setDefault: state.setDefaultProject,
        }

    fetch(url, { method: 'POST', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + uinf.token } })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case "OK":
                        let uinf: UserInfo = normalizeProjectApiUrls({ ...res.result });
                        handleLoginSuccess(uinf, true);

                        break;

                    case "CHALLENGE":

                        applyMfaChallenge(res.result, body.projectId);
                        setState({ ...state });

                        break;


                    default:

                        state.statusMessage = 'Select Project failed : ' + res.callStatusMessage;
                        state.loginBtnEnabled = true;
                        setState({ ...state });

                }
            })
            .catch(err => {
                state.statusMessage = 'Error SELECT PROJ : ' + err.toString();
                state.loginBtnEnabled = true;
                setState({ ...state });
            });
        //}
    }

    const cancelSelectProjectClick = (event: React.MouseEvent<HTMLButtonElement>) => {

        state.usrInf = new UserInfo();
        state.loginBtnEnabled = true;
        state.showProjectSelect = false;
        state.selectProjectBtnEnabled = false;
        state.statusMessage = '';
        setState({ ...state });

    }

    const setDefProjectChange = (e: CheckboxChangeEvent) => {

        state.setDefaultProject = !state.setDefaultProject;
        setState({ ...state });

    }

    const setProjectSelected = (e: GridRowClickEvent) => {
        let prj = state.usrInf;

        prj.userProjects.forEach((x) => { x.selected = false });
        let sel = prj.userProjects.find(x => x.projectId === e.dataItem.projectId)!;
        sel.selected = true;
        prj.currProject = sel;
        state.usrInf = prj;
        state.selectProjectBtnEnabled = true;
        setState({ ...state });

    }

    // Handle double-click on a project row: select the project and immediately continue
    const rowDoubleClick = (e: any) => {
        try {
            // Create a local copy of user info and mark the clicked project as selected
            let uinf = { ...state.usrInf } as UserInfo;
            if (uinf.userProjects && Array.isArray(uinf.userProjects)) {
                uinf.userProjects = uinf.userProjects.map((p) => ({ ...p, selected: p.projectId === e.dataItem.projectId }));
                uinf.currProject = uinf.userProjects.find(p => p.projectId === e.dataItem.projectId) || uinf.currProject;
            }

            // Update component state so UI reflects selection
            setState(prev => ({ ...prev, usrInf: uinf, selectProjectBtnEnabled: true }));

            // Call the same logic as the Continue button, passing the updated user info
            selectProjectClick(undefined, uinf);
        }
        catch (err) {
            // Swallow errors to avoid breaking UI; show message if necessary
            state.statusMessage = 'Error selecting project: ' + (err && err.toString ? err.toString() : String(err));
            setState({ ...state });
        }
    }

    const setDefaultUser = (userName: string) => {

        state.userName = userName;
        state.password = '';
        setState({ ...state });

    }


    const openMobileSettings = () => {
        if (typeof invokeOpenSettingsAction !== 'undefined') {
            invokeOpenSettingsAction();
        } else {
            alert("invokeOpenSettingsAction is not defined");
        }
    }



    const handleLogout = () => {
        // Clear app-specific cached credentials alongside MSAL's own token cache disposal
        sessionStorage.removeItem("defaultUser");

        instance.logoutPopup()
            .then(() => {
                console.log("User successfully logged out");
            })
            .catch(error => {
                console.error("Logout error:", error);
            });
    };


    return (
         <MsalProvider instance={instance}>
            <Dialog id="loginDlg" className="loginDlg" >

                <table id="loginTbl">
                    <tbody>
                        {(state.showForgotPwd) &&

                            <tr className="loginHdr">
                                <th colSpan={2}>Reset Password</th>
                            </tr>
                        }
                        {(state.showForgotPwd) &&
                            <tr>
                                {(!state.requestSent) &&
                                    <td colSpan={2} className="resetPwdTxt">
                                        <p><Button fillMode={"outline"} className="sendResetBtn" onClick={sendResetCodeClick}>Click here</Button> to send a reset code to your e-mail account.  </p>
                                        <p>Enter the reset code and a new password in the boxes below.</p>
                                        <p></p>
                                    </td>
                                }
                                {(state.requestSent) &&
                                    <td colSpan={2} className="resetPwdTxt">
                                        <p>An e-mail has been sent to your account.</p>
                                        <p>Enter the reset code and a new password in the boxes below.</p>
                                        <p><Button fillMode={"outline"} className="sendResetBtn" onClick={sendResetCodeClick}>Click here</Button> to request the reset code again.  </p>
                                    </td>
                                }
                            </tr>
                        }
                        {(state.showMFARequest) &&
                            <tr className="loginHdr">
                                <th colSpan={2}>Login Confirmation</th>
                            </tr>
                        }
                        {(state.showMFARequest && state.mFAMethod === 'Authenticator' && state.mFAEnrollRequired) &&
                            <tr>
                                <td colSpan={2} className="resetPwdTxt">
                                    <p>Scan the QR code below with your authenticator app (e.g. Google Authenticator, Microsoft Authenticator), then enter the 6-digit code it displays.</p>
                                    {state.mFAEnrollOtpAuthUrl &&
                                        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                                            <QRCodeSVG value={state.mFAEnrollOtpAuthUrl} size={160} />
                                        </div>
                                    }
                                    {state.mFAEnrollSecret &&
                                        <p>Can't scan the code? Enter this key manually: <strong>{state.mFAEnrollSecret}</strong></p>
                                    }
                                </td>
                            </tr>
                        }
                        {(state.showMFARequest && state.mFAMethod === 'Authenticator' && !state.mFAEnrollRequired) &&
                            <tr>
                                <td colSpan={2} className="resetPwdTxt">
                                    <p>Enter the code from your authenticator app in the box below.</p>
                                </td>
                            </tr>
                        }
                        {(state.showMFARequest && state.mFAMethod === 'Email') &&
                            <tr>
                                <td colSpan={2} className="resetPwdTxt">
                                    <p>An e-mail has been sent to your account.</p>
                                    <p>Enter the code from the email in the box below.</p>
                                    <p><Button fillMode={"outline"} className="sendResetBtn" onClick={resendMFACodeClick}>Click here</Button> to request the code again.  </p>
                                </td>

                            </tr>
                        }
                        {(!state.showMFARequest) &&
                            <tr className="loginHdr">
                                <th colSpan={2}>
                                    <table cellPadding="0" cellSpacing="0" >
                                        <tbody><tr>
                                            <td ></td>
                                            <th>Log In</th>
                                            <td>
                                                {props.pageInf.pageViewMode !== PageViewTypeEnum.Browser &&
                                                    <Button icon="gear" onClick={() => openMobileSettings()} title="Open Mobile App Settings" />
                                                }
                                            </td>
                                        </tr></tbody>
                                    </table>
                                </th>
                            </tr>
                        }
                        {(state.showMFARequest ? !state.showForgotPwd : (state.accessToken === "")) &&
                            <tr>
                                <td colSpan={2}>
                                    <fieldset className="credentialsGroup">
                                        <table className="credentialsTbl">
                                            <tbody>
                                                {(!state.showMFARequest && state.accessToken === "") &&
                                                    <tr>
                                                        <td className="loginLabelCell">Username : </td>
                                                        <td className="loginInputCell"><Input className="loginInput" id="loginUsername"  placeholder="Enter Username" required={true} disabled={loginPending || state.showProjectSelect} onChange={userNameChanged} value={state.userName} defaultValue={state.defaultUser} />  </td>
                                                    </tr>
                                                }
                                                {(!state.showForgotPwd && !state.showMFARequest && state.accessToken === "") &&
                                                    <tr>
                                                        <td className="loginLabelCell">Password : </td>
                                                        <td><Input className="loginInput" type="password" placeholder="Enter Password" required={true} disabled={loginPending || state.showProjectSelect} onChange={passwordChanged} value={state.password} onKeyDown={keyDownAction} />  </td>
                                                    </tr>
                                                }
                                                {(state.showForgotPwd && !state.showMFARequest && state.accessToken === "") &&
                                                    <tr>
                                                        <td className="loginLabelCell">New Password : </td>
                                                        <td><Input className="loginInput" type="password" placeholder="Enter Password" valid={state.passwordNewValid} title={state.passwordNewMsg} onChange={passwordNewChanged} value={state.passwordNew} />  </td>
                                                    </tr>
                                                }
                                                {(state.showForgotPwd && !state.showMFARequest && state.accessToken === "") &&
                                                    <tr>
                                                        <td className="loginLabelCell">Check Password : </td>
                                                        <td><Input className="loginInput" type="password" placeholder="Verify Password" valid={state.passwordChkValid} title={state.passwordChkMsg} onChange={passwordChkChanged} value={state.passwordChk} />  </td>
                                                    </tr>
                                                }
                                                {(state.showForgotPwd && !state.showMFARequest && state.accessToken === "") &&
                                                    <tr>
                                                        <td className="loginLabelCell">Reset Code : </td>
                                                        <td><Input className="loginInput" placeholder="Enter Reset Code" required={true} onChange={resetCodeChanged} />  </td>
                                                    </tr>
                                                }
                                                {(!state.showForgotPwd && state.showMFARequest) && //state.accessToken === "") &&
                                                    <tr>
                                                        <td className="loginLabelCell">Code : </td>
                                                        <td><Input className="loginInput" placeholder={state.mFAMethod === 'Authenticator' ? "Enter Authenticator Code" : "Enter Code"} required={true} onChange={mFACodeChanged} />  </td>
                                                    </tr>
                                                }

                                                {(!state.showMFARequest && !state.showForgotPwd && state.accessToken === "") &&
                                                    <tr>
                                                        <td colSpan={2} className="loginBtnCell"><Button fillMode={"outline"} className="loginBtn" disabled={loginPending || !state.loginBtnEnabled} onClick={loginClick} >Log In</Button></td>
                                                    </tr>
                                                }
                                                {(state.showForgotPwd && !state.showMFARequest && state.accessToken === "") &&
                                                    <tr>
                                                        <td colSpan={2} className="loginBtnCell"><Button fillMode={"outline"} className="loginBtn" disabled={!state.loginResetBtnEnabled} onClick={loginResetClick} >Reset Password / Log In</Button></td>
                                                    </tr>
                                                }
                                                {(!state.showForgotPwd && state.showMFARequest) && //state.accessToken === "") &&
                                                    <tr>
                                                        <td colSpan={2} className="loginBtnCell"><Button fillMode={"outline"} className="loginBtn" disabled={!state.loginResetBtnEnabled} onClick={mFAResetClick} >{state.mFAEnrollRequired ? "Verify & Enable" : "Log In"}</Button></td>
                                                    </tr>
                                                }
                                            </tbody>
                                        </table>
                                    </fieldset>
                                </td>
                            </tr>
                        }
                        {(!state.showForgotPwd && !state.showMFARequest && !state.showProjectSelect && state.accessToken === "") &&
                            <tr>
                                <td colSpan={2} className="msOrDivider"><span>OR</span></td>
                            </tr>
                        }
                        {(!state.showForgotPwd && !state.showMFARequest && !state.showProjectSelect && state.accessToken === "") &&
                            <tr>
                                <td colSpan={2} className="loginBtnCell">
                                    <button
                                        type="button"
                                        className="msSignInBtn"
                                        disabled={loginPending}
                                        onClick={microsoftLoginClick}
                                        aria-label="Sign in with Microsoft"
                                    >
                                        <MicrosoftSignInLogo className="msSignInLogo" focusable="false" aria-hidden="true" />
                                    </button>
                                </td>
                            </tr>
                        }
                        {(!state.showProjectSelect && !state.showMFARequest && state.accessToken === "") &&
                            <tr>
                                <td colSpan={2}>
                                    <table className="optionsTbl">
                                        <tbody>
                                            <tr>
                                                <td><Checkbox className="defProjCbx" disabled={loginPending} onChange={useDefProjChanged} value={state.useDefaultProject} /></td>
                                                <td>Use Default Project </td>
                                                <td align="right" ><Button fillMode={"flat"} className="forgotPwdButton" disabled={loginPending || state.showProjectSelect} onClick={forgotPwdClick}>Forgot Password</Button> </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        }
                        {(state.showProjectSelect && !state.showMFARequest) &&
                            <tr className="loginHdr">
                                <th colSpan={2}>Select Project</th>
                            </tr>
                        }
                        {(state.showProjectSelect && !state.showMFARequest) &&

                            <tr>
                                <td colSpan={2} >
                                    {/* Wrap the Grid in a scrollable container so the dialog can be limited in height */}
                                    <div className="projectListContainer">
                                        <Grid
                                            data={(state.usrInf && state.usrInf.userProjects) ? state.usrInf.userProjects.map((project) =>
                                                (state.usrInf && state.usrInf.currProject && project.projectId === state.usrInf.currProject.projectId)
                                                    ? { ...project, selected: true }
                                                    : { ...project, selected: false }
                                            ) : []}
                                            selectedField="selected"
                                            selectable={{
                                                enabled: true,
                                                // Disable drag selection so touch-drag can scroll the list on mobile devices
                                                drag: false,
                                                cell: false,
                                                mode: "single",
                                            }}

                                            onRowClick={setProjectSelected}
                                            onRowDoubleClick={rowDoubleClick}
                                        >
                                            <GridColumn field="projectName" headerCell={(e) => null} />
                                        </Grid>
                                    </div>
                                </td>
                            </tr>
                        }
                        {(state.showProjectSelect && !state.showMFARequest) &&
                            <tr>
                                <td colSpan={2}>
                                    <table width="100%">
                                        <tbody>
                                            <tr>
                                                <td className="loginBtnCell" ><Button fillMode={"outline"} disabled={!state.selectProjectBtnEnabled} onClick={selectProjectClick} >Continue</Button></td>

                                                {(state.accessToken === "") &&
                                                    <td className="loginBtnCell" ><Button fillMode={"outline"} onClick={cancelSelectProjectClick} >Cancel</Button></td>
                                                }
                                            </tr>
                                            <tr>
                                                <td colSpan={2} className="defProjRow" >
                                                    <div className="defProjRowContent">
                                                        <Checkbox className="defProjCbx" defaultChecked={false} onChange={setDefProjectChange} />
                                                        <span className="defProjLabel">Set as Default Project</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        }
                        {(state.statusMessage !== '') &&
                            <tr>
                                <td colSpan={2} className="loginMsgCell">{state.statusMessage}</td>
                            </tr>
                        }
                    </tbody>
                </table>
            </Dialog>
        </MsalProvider>
    )
}


export default connector(Login);
